import { Component, computed, inject, input, model, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { rxResourceFixed } from '@zayan-erp/shared';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DepositService, importDeposit } from '@zayan-erp/sales';
import {
  AbilityService,
  CurrencyInputFieldComponent,
  DropdownData,
  GenericDropdownComponent,
  LookupsService
} from '@zayan-erp/shared';
import { TableModule } from '@openng/optimus-ui/table';
import { DatePickerModule } from '@openng/optimus-ui/datepicker';
import { of } from 'rxjs';

@Component({
  selector: 'lib-imports-model',
  imports: [
    GenericDropdownComponent,
    ReactiveFormsModule,
    FormsModule,
    TableModule,
    DatePickerModule,
    CurrencyInputFieldComponent
  ],
  standalone: true,
  templateUrl: './imports-model.component.html',
  styleUrl: './imports-model.component.scss'
})
export class ImportsModelComponent {
  importType = input<string>();
  #lookupsService = inject(LookupsService);
  _depositService = inject(DepositService);
  #abilityService = inject(AbilityService);

  readonly canWriteDeals = computed(() => this.#abilityService.ability.can('write-sales-deals', 'deals'));

  depositTypes = toSignal(this._depositService.getDepositTypes(), { initialValue: [] as DropdownData[] });
  tasks = toSignal(this.#lookupsService.readTasks(), { initialValue: [] as DropdownData[] });
  selectedLot = output<string>();
  lots = computed(() => this.getLots.value() ?? []);
  getLots = rxResourceFixed({
    params: () => ({ dealId: this._depositService.dealId() ?? '' }),
    defaultValue: [],
    stream: ({ params }) => {
      if (!params.dealId) {
        return of([]);
      }
      return this._depositService.dealsByDealId(params.dealId);
    }
  });

  onLotSelected(lotId: string | null | undefined): void {
    const lotIdString = lotId ? String(lotId) : '';
    this.selectedLot.emit(lotIdString);
  }

  onTaskChange(index: number, taskId: string) {
    if (!this.canWriteDeals()) return;
    const deposits = this._depositService.deposits();
    if (deposits[index]) {
      deposits[index].taskId = taskId;
      this._depositService.deposits.set([...deposits]);
    }
  }

  onDateChange(index: number, date: Date | null) {
    if (!this.canWriteDeals()) return;
    const deposits = this._depositService.deposits();
    if (deposits[index]) {
      // Keep as Date object for the datepicker, will be converted to string when saving
      (deposits[index] as importDeposit).depositDueDate = date?.toISOString() ?? '';
      this._depositService.deposits.set([...deposits]);
    }
  }

  onAmountChange(index: number, amount: number | null) {
    if (!this.canWriteDeals()) return;
    const deposits = this._depositService.deposits();
    if (deposits[index]) {
      deposits[index].depositAmount = amount ?? 0;
      this._depositService.deposits.set([...deposits]);
    }
  }

  getDepositTypeName(depositTypeId: string | undefined): string {
    if (!depositTypeId) return '';
    const depositType = this.depositTypes().find(type => type.id === depositTypeId);
    return depositType?.firstLabel ?? '';
  }
}
